import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";
import SEO from "../components/SEO";
import useReveal from "../hooks/useReveal";
import { SITE } from "../config/site";

const MAP_SRC =
  "https://www.google.com/maps?q=Fulya%2C%20B%C3%BCy%C3%BCkdere%20Cd.%20Lale%20%C4%B0%C5%9F%20Merkezi%20No%3A%2062%2C%20%C5%9Ei%C5%9Fli%2C%20%C4%B0stanbul&output=embed";

export default function Contact() {
  const { t } = useTranslation();
  const pageRef = useRef(null);
  useReveal(pageRef, [t]);

  const chips = t("contact.form.chips", { returnObjects: true });
  const budgetOpts = t("contact.form.budget_opts", { returnObjects: true });
  const [active, setActive] = useState(() => new Set());
  const toggle = (k) =>
    setActive((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  const submit = (e) => {
    e.preventDefault();
    window.alert(t("contact.form.success"));
    e.target.reset();
    setActive(new Set());
  };

  const contactLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    mainEntity: { "@type": "Organization", name: SITE.name, telephone: SITE.phone, email: SITE.email },
  };

  return (
    <div ref={pageRef}>
      <SEO title={t("contact.meta_title")} description={t("contact.meta_desc")} jsonLd={contactLd} />

      <section className="page-hero blueprint" style={{ borderBottom: 0 }}>
        <div className="container ph-inner">
          <span className="eyebrow reveal">{t("contact.hero.eyebrow")}</span>
          <h1 className="reveal d1">{t("contact.hero.title_1")}<span className="accent">{t("contact.hero.title_hl")}</span></h1>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 20 }}>
        <div className="container">
          <div className="contact-grid reveal">
            {/* FORM */}
            <div className="contact-form-wrap">
              <span className="eyebrow form-eyebrow">{t("contact.form.eyebrow")}</span>
              <div className="form-title">{t("contact.form.title")}</div>
              <form className="cform" onSubmit={submit}>
                <div className="field-2">
                  <div className="field"><label>{t("contact.form.name")}</label><input type="text" placeholder={t("contact.form.name_ph")} required /></div>
                  <div className="field"><label>{t("contact.form.company")}</label><input type="text" placeholder={t("contact.form.company_ph")} /></div>
                </div>
                <div className="field-2">
                  <div className="field"><label>{t("contact.form.email")}</label><input type="email" placeholder={t("contact.form.email_ph")} required /></div>
                  <div className="field"><label>{t("contact.form.phone")}</label><input type="tel" placeholder={t("contact.form.phone_ph")} /></div>
                </div>
                <div className="field">
                  <label>{t("contact.form.budget")}</label>
                  <select defaultValue="">{budgetOpts.map((o, i) => <option key={i} value={i === 0 ? "" : o}>{o}</option>)}</select>
                </div>
                <div className="field">
                  <label>{t("contact.form.interest")}</label>
                  <div className="chip-row">
                    {chips.map((c) => (
                      <span key={c.k} className={`chip${active.has(c.k) ? " on" : ""}`} onClick={() => toggle(c.k)}>{c.v}</span>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <label>{t("contact.form.message")}</label>
                  <textarea placeholder={t("contact.form.message_ph")} required></textarea>
                </div>
                <div className="submit-row">
                  <p>{t("contact.form.note")}</p>
                  <button type="submit" className="btn">{t("contact.form.submit")} <FiArrowRight className="arrow" /></button>
                </div>
              </form>
            </div>

            {/* INFO */}
            <div className="contact-info">
              <div className="info-block">
                <div className="lab">{t("contact.info.direct_lab")}</div>
                <h4>{t("contact.info.direct_title")}</h4>
                <p>
                  <a href={`tel:${SITE.phoneRaw}`}>{SITE.phone}</a><br />
                  <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
                </p>
              </div>
              <div className="info-block">
                <div className="lab">{t("contact.info.addr_lab")}</div>
                <h4>{t("contact.info.addr_title")}</h4>
                <p>{SITE.addressLine}</p>
              </div>
              <div className="info-block">
                <div className="lab">{t("contact.info.hours_lab")}</div>
                <h4>{t("contact.info.hours_title")}</h4>
                <p>{t("contact.info.hours_1")}<br />{t("contact.info.hours_2")}<br />{t("contact.info.hours_3")}</p>
              </div>
              <div className="info-block">
                <div className="lab">{t("contact.info.social_lab")}</div>
                <h4>{t("contact.info.social_title")}</h4>
                <p>
                  <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a> ·{" "}
                  <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer">Instagram</a> ·{" "}
                  <a href={SITE.social.youtube} target="_blank" rel="noopener noreferrer">YouTube</a>
                </p>
              </div>
            </div>
          </div>

          {/* MAP */}
          <div className="contact-map reveal">
            <iframe src={MAP_SRC} title={t("contact.map.city")} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen></iframe>
          </div>

          {/* WA BANNER */}
          <div className="wa-banner reveal">
            <h3>{t("contact.banner.title_1")} <span className="it">{t("contact.banner.title_hl")}</span></h3>
            <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="btn on-ink">{t("contact.banner.cta")} <FiArrowUpRight className="arrow" /></a>
          </div>
        </div>
      </section>
    </div>
  );
}
